import { type EnvironmentId } from "@cmmd-forge/contracts";
import { projectScriptCwd } from "@cmmd-forge/shared/projectScripts";
import { type TaskContextModel } from "../command-center/TaskContextSurface";
import { useCanonicalTaskContextModel } from "../command-center/useCanonicalTaskContextModel";
import { type Project, type Thread } from "../../types";
import { useGitStatus } from "~/lib/gitStatusState";

export function useChatWorkspaceContext(input: {
  readonly environmentId: EnvironmentId;
  readonly activeThread: Thread | undefined;
  readonly activeProject: Project | null | undefined;
}) {
  const { environmentId, activeThread, activeProject } = input;
  const workspace = useChatGitWorkspace({
    environmentId,
    activeThread,
    activeProject,
  });
  const activeTaskContextModel = useChatTaskContextModel({ activeThread });

  return {
    ...workspace,
    activeTaskContextModel,
  };
}

function useChatGitWorkspace(input: {
  readonly environmentId: EnvironmentId;
  readonly activeThread: Thread | undefined;
  readonly activeProject: Project | null | undefined;
}) {
  const { environmentId, activeThread, activeProject } = input;
  const gitCwd = activeProject
    ? projectScriptCwd({
        project: { cwd: activeProject.cwd },
        worktreePath: activeThread?.worktreePath ?? null,
      })
    : null;
  const gitStatusQuery = useGitStatus({ environmentId, cwd: gitCwd });
  const activeProjectCwd = activeProject?.cwd ?? null;
  const activeThreadWorktreePath = activeThread?.worktreePath ?? null;
  const activeWorkspaceRoot = activeThreadWorktreePath ?? activeProjectCwd ?? undefined;
  const isGitRepo = gitStatusQuery.data?.isRepo ?? true;
  const gitStatusKnown = gitStatusQuery.data !== null;

  return {
    gitCwd,
    gitStatusQuery,
    activeProjectCwd,
    activeThreadWorktreePath,
    activeWorkspaceRoot,
    isGitRepo,
    gitStatusKnown,
  };
}

function useChatTaskContextModel(input: {
  readonly activeThread: Thread | undefined;
}): TaskContextModel | null {
  const taskId = input.activeThread?.anchorKind === "task" ? input.activeThread.anchorCmmdId : null;
  return useCanonicalTaskContextModel(taskId);
}
